import { Checkbox, Field, Hint, Input, Label } from '@zendeskgarden/react-forms'
import React, { useState } from 'react'
import { NavigateFunction } from 'react-router'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { useRerenderOnMutation_deprecated } from '../../hooks/useRerenderOnMutation'
import { Item as PSItem } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { currencyFloat, filterMonetaryInput, filterNumericInput } from '../../utils/numbers'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButtonDanger, PSButtonPrimary } from '../app/PSButton'
import { PSLearnMore } from '../app/PSLearnMore'
import { PSTextarea } from '../app/PSTextarea'
import Paragraph from '../app/design/Paragraph'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { SettingsPhotoField } from '../settings/SettingsPhotoField'
import { usePhotoUpload } from '../settings/hooks'

type ProductPageProps = {
    forOnboarding?: boolean
    item?: PSItem
    completeCallBack?: (record: PSItem) => void
}

type ProductPageData = {
    id?: string
    item?: PSItem
    record: PSItem
    forOnboarding?: boolean
    completeCallBack?: (record: PSItem) => void
}

const viewWillAppear = async (
    base: PocketComponent<ProductPageProps>,
    navigate?: NavigateFunction
) => {
    let item = base.props.item
    if (!!base.urlParams.productID) {
        item = await RecordFactory.getInstance().loadItem(base.urlParams.productID)
        if (!item) {
            navigate?.('/')
            return
        }
    }
    let record: PSItem | undefined = item?.clone()
    if (!record) {
        record = new PSItem()
        record.type = 'product'
        record.isPublic = true
        record.allowAddon = true
        record.allowInventory = true
    }
    const data: ProductPageData = {
        id: record.id,
        item: item,
        record: record,
        forOnboarding: base.props.forOnboarding,
        completeCallBack: base.props.completeCallBack,
    }
    if (base.urlParams.action === 'copy') {
        data.record.id = undefined
        data.record.name = undefined
    }

    return data
}

const render = ({ data, base }: PocketComponentProps<ProductPageData, ProductPageProps>) => {
    const record = useRerenderOnMutation_deprecated(data.record)

    const navigate = usePSNavigate()

    const [photo, setPhoto] = useState<undefined | string | File>(record.image?.url)
    const [addPhotoRef, selectPhoto, selectedPhoto] = usePhotoUpload({ disabled: false, setPhoto })

    const [isSaving, setIsSaving] = useState(false)

    const [learnMoreComponent, showLearnMore] = PSLearnMore([
        {
            key: 'cost',
            title: 'About total cost',
            body: 'Total cost is the total amount of money you paid to buy the specified quantity of this product. Total cost = Quantity x Unit cost',
        },
    ])

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

    function checkRecordPerm() {
        return (
            !!record.name &&
            !!record.rate &&
            (record.quantity === undefined || record.quantity >= 1) &&
            (record.cost === undefined || record.cost >= 0)
        )
    }

    function hasEdits() {
        if (record.name !== data.item?.name) return true
        if (record.rate !== data.item?.rate) return true
        if ((record.memo ?? '') !== (data.item?.memo ?? '')) return true
        if (record.quantity !== data.item?.quantity) return true
        if (record.isPublic !== (data.item?.isPublic ?? true)) return true
        if (record.allowInventory !== (data.item?.allowInventory ?? true)) return true
        if (photo !== data.item?.image?.url) return true
        return false
    }

    function saveEnabled() {
        return hasEdits() && checkRecordPerm()
    }

    async function submitChanges() {
        if (checkRecordPerm()) {
            if (data.forOnboarding && data.completeCallBack) {
                data.completeCallBack(record)
                return
            }
            base.showProgressBar('Saving..')
            setIsSaving(true)
            const result = await RecordFactory.getInstance().sendItem(
                record,
                photo instanceof File ? photo : undefined
            )
            base.hideProgressBar()
            base.hideProgressBar()
            if (result.ok) {
                if (data.completeCallBack) data.completeCallBack(result.value)
                else navigate(-1)
            }
            else {
                setIsSaving(false)
                base.logAndDisplayError(result.error)
            }
        }
    }

    async function onDelete() {
        setShowDeleteConfirmation(false)
        base.showProgressBar('Deleting..')
        const result = await RecordFactory.getInstance().deleteItem(data.id!, false)
        base.hideProgressBar()
        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }
        if (data.completeCallBack) data.completeCallBack(result.value)
        else {
            navigate(-1)
        }
    }

    const showDeleteButton = !!data.id && base.urlParams.action !== 'copy' && !data.forOnboarding

    return (
        <div className="servicesSettings">
            <div className="header">
                <h1>Product</h1>
                {showDeleteButton && (
                    <PSButtonDanger
                        style={{ height: '40px' }}
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Delete product
                    </PSButtonDanger>
                )}
                <PSButtonPrimary
                    style={{ height: '40px', marginLeft: showDeleteButton ? '16px' : 'auto' }}
                    onClick={submitChanges}
                    disabled={!saveEnabled()}
                >
                    {'Save'}
                </PSButtonPrimary>
            </div>

            <div className="form">
                {!data.forOnboarding && (
                    <SettingsPhotoField
                        photo={photo}
                        selectPhoto={selectPhoto}
                        selectedPhoto={selectedPhoto}
                        addPhotoRef={addPhotoRef}
                        title="Service"
                    />
                )}

                <Field className="field">
                    <Label className="label">Name (required)</Label>
                    <Input
                        placeholder="Product name"
                        value={record.name}
                        onChange={(e) => (record.name = e.target.value)}
                    />
                </Field>

                <Field className="field">
                    <Label className="label">Price · $ (required)</Label>
                    <Input
                        placeholder="Product price"
                        value={record.rate}
                        onChange={(e) =>
                            (record.rate = currencyFloat(filterMonetaryInput(e.target.value)))
                        }
                        type="number"
                        min={0}
                        step={0.01}
                    />
                </Field>

                <Field className="field">
                    <Label className="label">Description</Label>
                    <PSTextarea
                        placeholder="Product description"
                        className="input"
                        minRows={8}
                        maxLength={1500}
                        value={record.memo ?? ''}
                        onChange={(value) => (record.memo = value)}
                    />
                </Field>

                <h2>Details</h2>

                <Field className="field">
                    <Checkbox
                        checked={record.isPublic}
                        onChange={(e) => {
                            record.isPublic = e.target.checked
                        }}
                    >
                        <Label className="withHint">Show online</Label>
                        <Hint>Product will appear to clients after booking you online</Hint>
                    </Checkbox>
                </Field>

                <Field className="field">
                    <Checkbox
                        checked={record.allowInventory}
                        onChange={(e) => {
                            record.allowInventory = e.target.checked
                        }}
                    >
                        <Label className="withHint">Track inventory</Label>
                        <Hint>Products sold will automatically deduct from your inventory</Hint>
                    </Checkbox>
                </Field>

                {!data.id && record.allowInventory && (
                    <>
                        <Field className="field">
                            <Label className="label">Quantity</Label>
                            <Hint className="hint">
                                Add the total number of products you have available
                            </Hint>
                            <Input
                                placeholder="Quantity"
                                value={record.quantity}
                                onChange={(e) =>
                                    (record.quantity = parseInt(filterNumericInput(e.target.value)))
                                }
                            />
                        </Field>

                        <Field className="field">
                            <Label className="label">Total cost</Label>
                            <Input
                                placeholder="Total cost"
                                value={record.cost}
                                onChange={(e) =>
                                    (record.cost = currencyFloat(
                                        filterMonetaryInput(e.target.value)
                                    ).toFixed(2))
                                }
                            />
                        </Field>

                        <div
                            className="buttonLink brightBlue"
                            style={{ display: 'inline-block', paddingTop: 8 }}
                            onClick={() => showLearnMore('cost')}
                        >
                            Learn more &gt;
                        </div>
                    </>
                )}
            </div>
            {showDeleteConfirmation && (
                <GuardrailModal
                    title="Are you sure?"
                    buttonText="Delete"
                    onClose={() => setShowDeleteConfirmation(false)}
                    onAction={onDelete}
                >
                    <Paragraph>Are you sure you{"'"}d like to delete this item?</Paragraph>
                </GuardrailModal>
            )}
            {learnMoreComponent}

            <NavigationBlockerPrompt when={!!base.urlParams.productID && hasEdits() && !isSaving} />
        </div>
    )
}

/** @deprecated - Switch to ProductPage instead. This component will be removed in a future release. */
export const ProductPageV1 = usePSComponent<ProductPageProps>(render, viewWillAppear)
